import { type FC, type ReactNode, useEffect, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Box } from '../Box';
import { Heading } from '../Typography';
import {
  chartCanvasStyles,
  chartHeaderStyles,
  chartSurfaceStyles,
  chartWrapperStyles,
  compactYAxisLabelStyles,
  emptyStateStyles,
  tooltipLabelStyles,
  tooltipRowStyles,
  tooltipSeriesStyles,
  tooltipStyles,
  tooltipSwatchStyles,
} from './Chart.styles';
import {
  type ChartAxisConfig,
  type ChartSeries,
  type ChartType,
  type ChartValue,
  type ChartValueAxisConfig,
} from './Chart.types';

export type ChartProps = {
  ariaLabel?: string;
  className?: string;
  connectNulls?: boolean;
  emptyState?: ReactNode;
  height?: number;
  series: ChartSeries[];
  showDots?: boolean;
  showLegend?: boolean;
  title?: string;
  type: ChartType;
  xAxis?: ChartAxisConfig;
  yAxis?: ChartValueAxisConfig;
};

type ChartDataRow = {
  x: ChartValue;
  xLabel: string;
} & Record<string, ChartValue | number | null>;

type SeriesConfig = {
  color: string;
  dataKey: string;
  id: string;
  label: string;
};

type TooltipPayload = {
  color?: string;
  dataKey?: string;
  name?: string;
  value?: number;
};

type CustomTooltipProps = {
  active?: boolean;
  label?: ChartValue;
  payload?: TooltipPayload[];
};

const DEFAULT_COLORS = [
  'var(--color-primary-500)',
  'var(--color-yellow-700)',
  'var(--color-accent-500)',
  'var(--color-success-500)',
  'var(--color-neutral-700)',
  'var(--color-brown-600)',
] as const;

const COMPACT_CHART_WIDTH = 520;

const getDefaultColor = (index: number) =>
  DEFAULT_COLORS[index % DEFAULT_COLORS.length] ?? DEFAULT_COLORS[0];

const isNumericValue = (value: ChartValue): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const sortChartValues = (values: ChartValue[]) => {
  const allNumeric = values.every(isNumericValue);

  return [...values].sort((a, b) => {
    if (allNumeric && isNumericValue(a) && isNumericValue(b)) {
      return a - b;
    }

    return String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  });
};

const uniqueXValues = (series: ChartSeries[]) => {
  const seen = new Set<string>();
  const values: ChartValue[] = [];

  series.forEach((seriesItem) => {
    seriesItem.points.forEach((point) => {
      const key = String(point.x);

      if (!seen.has(key)) {
        seen.add(key);
        values.push(point.x);
      }
    });
  });

  return sortChartValues(values);
};

const formatValue = (value: number, axis?: ChartValueAxisConfig) => {
  if (axis?.tickFormatter) {
    return axis.tickFormatter(value);
  }

  const formatted = Number.isInteger(value)
    ? value.toLocaleString()
    : value.toLocaleString(undefined, { maximumFractionDigits: 1 });

  return axis?.unit === 'percent' ? `${formatted}%` : formatted;
};

const formatXValue = (value: ChartValue, axis?: ChartAxisConfig) => {
  if (axis?.tickFormatter) {
    return axis.tickFormatter(value);
  }

  return String(value);
};

const getYDomain = (
  series: ChartSeries[],
  axis?: ChartValueAxisConfig
): [number | 'auto', number | 'auto'] => {
  if (axis?.min !== undefined || axis?.max !== undefined) {
    return [axis?.min ?? 'auto', axis?.max ?? 'auto'];
  }

  const values = series.flatMap((seriesItem) =>
    seriesItem.points
      .map((point) => point.y)
      .filter(
        (value): value is number => value !== null && Number.isFinite(value)
      )
  );
  const maxValue = values.length ? Math.max(...values) : 0;

  if (axis?.unit === 'percent' && maxValue <= 100) {
    return [0, 100];
  }

  return [0, 'auto'];
};

const getSeriesConfigs = (series: ChartSeries[]): SeriesConfig[] =>
  series.map((seriesItem, index) => ({
    color: seriesItem.color ?? getDefaultColor(index),
    dataKey: `series_${index}`,
    id: seriesItem.id,
    label: seriesItem.label,
  }));

const buildChartData = (
  series: ChartSeries[],
  seriesConfigs: SeriesConfig[],
  xAxis?: ChartAxisConfig
) => {
  const xValues = uniqueXValues(series);

  return xValues.map((xValue) => {
    const row: ChartDataRow = {
      x: xValue,
      xLabel: formatXValue(xValue, xAxis),
    };

    series.forEach((seriesItem, index) => {
      const point = seriesItem.points.find(
        (seriesPoint) => String(seriesPoint.x) === String(xValue)
      );

      const seriesConfig = seriesConfigs[index];

      if (seriesConfig) {
        row[seriesConfig.dataKey] = point?.y ?? null;
      }
    });

    return row;
  });
};

const hasChartData = (series: ChartSeries[]) =>
  series.some((seriesItem) =>
    seriesItem.points.some(
      (point) => point.y !== null && Number.isFinite(point.y)
    )
  );

const CustomTooltip: FC<
  CustomTooltipProps & {
    xAxis?: ChartAxisConfig;
    yAxis?: ChartValueAxisConfig;
  }
> = ({ active, label, payload, xAxis, yAxis }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const visiblePayload = payload.filter(
    (item) => item.value !== null && item.value !== undefined
  );

  if (visiblePayload.length === 0) {
    return null;
  }

  return (
    <div css={tooltipStyles}>
      {label !== undefined && (
        <p css={tooltipLabelStyles}>
          {xAxis?.label ? `${xAxis.label}: ` : ''}
          {label}
        </p>
      )}
      {visiblePayload.map((item) => (
        <p key={`${item.dataKey ?? item.name}`} css={tooltipRowStyles}>
          <span css={tooltipSeriesStyles}>
            <span
              css={tooltipSwatchStyles}
              style={{ backgroundColor: item.color }}
            />
            <span>{item.name}</span>
          </span>
          <strong>{formatValue(Number(item.value), yAxis)}</strong>
        </p>
      ))}
    </div>
  );
};

const Chart: FC<ChartProps> = ({
  ariaLabel,
  className,
  connectNulls = false,
  emptyState = 'No chart data available',
  height = 320,
  series,
  showDots = true,
  showLegend = true,
  title,
  type,
  xAxis,
  yAxis,
}) => {
  const [isClient, setIsClient] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const chartSurfaceRef = useRef<HTMLDivElement>(null);

  useEffect(() => setIsClient(true), []);

  useEffect(() => {
    const element = chartSurfaceRef.current;
    if (!element) return;

    const updateCompactMode = (width: number) => {
      setIsCompact(width < COMPACT_CHART_WIDTH);
    };

    updateCompactMode(element.getBoundingClientRect().width);

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) updateCompactMode(entry.contentRect.width);
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const hasData = hasChartData(series);
  const seriesConfigs = getSeriesConfigs(series);
  const data = buildChartData(series, seriesConfigs, xAxis);
  const yDomain = getYDomain(series, yAxis);
  const hasLegend = showLegend && series.length > 1;
  const xAxisHeight = xAxis?.label ? 52 : 32;
  const showCompactYAxisLabel = isCompact && Boolean(yAxis?.label);
  const yAxisWidth = isCompact ? 48 : yAxis?.label ? 78 : 56;
  const commonChartProps = {
    data,
    margin: {
      top: 8,
      right: 20,
      bottom: hasLegend ? 8 : 4,
      left: isCompact ? 0 : 8,
    },
  };

  if (!hasData || data.length === 0) {
    return (
      <Box css={chartWrapperStyles} className={className}>
        {title && (
          <Box css={chartHeaderStyles}>
            <Heading size="2xs" fontWeight="bold">
              {title}
            </Heading>
          </Box>
        )}
        <Box css={emptyStateStyles}>{emptyState}</Box>
      </Box>
    );
  }

  return (
    <Box css={chartWrapperStyles} className={className}>
      {title && (
        <Box css={chartHeaderStyles}>
          <Heading size="2xs" fontWeight="bold">
            {title}
          </Heading>
        </Box>
      )}

      <Box
        ref={chartSurfaceRef}
        css={chartSurfaceStyles}
        height={height}
        role={ariaLabel ? 'img' : undefined}
        aria-label={ariaLabel}
      >
        {showCompactYAxisLabel && (
          <div css={compactYAxisLabelStyles}>{yAxis?.label}</div>
        )}
        {isClient && (
          <Box css={chartCanvasStyles}>
            <ResponsiveContainer width="100%" height="100%">
              {type === 'line' ? (
                <LineChart {...commonChartProps}>
                  <CartesianGrid
                    stroke="var(--color-neutral-200)"
                    strokeDasharray="4 4"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="xLabel"
                    axisLine={{ stroke: 'var(--border-primary)' }}
                    height={xAxisHeight}
                    minTickGap={xAxis?.minTickGap}
                    tickLine={false}
                    label={
                      xAxis?.label
                        ? {
                            value: xAxis.label,
                            position: 'insideBottom',
                            offset: 4,
                            fill: 'var(--text-secondary)',
                            fontSize: 12,
                          }
                        : undefined
                    }
                  />
                  <YAxis
                    axisLine={{ stroke: 'var(--border-primary)' }}
                    tickLine={false}
                    tickFormatter={(value) => formatValue(Number(value), yAxis)}
                    width={yAxisWidth}
                    domain={yDomain}
                    label={
                      yAxis?.label && !isCompact
                        ? {
                            value: yAxis.label,
                            angle: -90,
                            position: 'left',
                            offset: 4,
                            fill: 'var(--text-secondary)',
                            fontSize: 12,
                          }
                        : undefined
                    }
                  />
                  <Tooltip
                    content={<CustomTooltip xAxis={xAxis} yAxis={yAxis} />}
                    cursor={{ stroke: 'var(--color-neutral-300)' }}
                  />
                  {hasLegend && <Legend verticalAlign="bottom" height={28} />}
                  {seriesConfigs.map((seriesConfig) => (
                    <Line
                      key={seriesConfig.id}
                      dataKey={seriesConfig.dataKey}
                      name={seriesConfig.label}
                      type="monotone"
                      stroke={seriesConfig.color}
                      strokeWidth={3}
                      dot={showDots ? { r: 3, strokeWidth: 2 } : false}
                      activeDot={{ r: 5 }}
                      connectNulls={connectNulls}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              ) : (
                <BarChart {...commonChartProps}>
                  <CartesianGrid
                    stroke="var(--color-neutral-200)"
                    strokeDasharray="4 4"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="xLabel"
                    axisLine={{ stroke: 'var(--border-primary)' }}
                    height={xAxisHeight}
                    minTickGap={xAxis?.minTickGap}
                    tickLine={false}
                    label={
                      xAxis?.label
                        ? {
                            value: xAxis.label,
                            position: 'insideBottom',
                            offset: 4,
                            fill: 'var(--text-secondary)',
                            fontSize: 12,
                          }
                        : undefined
                    }
                  />
                  <YAxis
                    axisLine={{ stroke: 'var(--border-primary)' }}
                    tickLine={false}
                    tickFormatter={(value) => formatValue(Number(value), yAxis)}
                    width={yAxisWidth}
                    domain={yDomain}
                    label={
                      yAxis?.label && !isCompact
                        ? {
                            value: yAxis.label,
                            angle: -90,
                            position: 'left',
                            offset: 4,
                            fill: 'var(--text-secondary)',
                            fontSize: 12,
                          }
                        : undefined
                    }
                  />
                  <Tooltip
                    content={<CustomTooltip xAxis={xAxis} yAxis={yAxis} />}
                    cursor={{ fill: 'rgba(32, 35, 39, 0.06)' }}
                  />
                  {hasLegend && <Legend verticalAlign="bottom" height={28} />}
                  {seriesConfigs.map((seriesConfig) => (
                    <Bar
                      key={seriesConfig.id}
                      dataKey={seriesConfig.dataKey}
                      name={seriesConfig.label}
                      fill={seriesConfig.color}
                      radius={[4, 4, 0, 0]}
                      isAnimationActive={false}
                    />
                  ))}
                </BarChart>
              )}
            </ResponsiveContainer>
          </Box>
        )}
      </Box>
    </Box>
  );
};

Chart.displayName = 'Chart';

export default Chart;
